import { useState } from "react";
import useSessionStore from "../state/session";
import { Link } from "react-router-dom";
import { getCurrentAppBuildIdentity } from "../lib/appVersion";
import ServiceStatus, { type ServiceStatusSnapshot } from "./ServiceStatus";
import SupabaseStatus from "./SupabaseStatus";

function shortBuildValue(value: string, fallback = 'local'): string {
  const normalized = value.trim();
  if (!normalized) return fallback;
  return normalized.slice(0, 8);
}

function displayEnvironment(value: string): string {
  const normalized = value.trim().toLowerCase() || 'production';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function portalBuildVersion(buildTime: string, commit: string): string {
  const shortCommit = shortBuildValue(commit);
  const parsed = new Date(buildTime);
  if (Number.isNaN(parsed.getTime())) return shortCommit;
  const date = [
    parsed.getUTCFullYear(),
    String(parsed.getUTCMonth() + 1).padStart(2, '0'),
    String(parsed.getUTCDate()).padStart(2, '0'),
  ].join('.');
  return `${date}+${shortCommit}`;
}

interface FooterProps {
  showItemsDebugToggle?: boolean;
  itemsDebugEnabled?: boolean;
  onToggleItemsDebug?: () => void;
}

export default function Footer({
  showItemsDebugToggle = false,
  itemsDebugEnabled = false,
  onToggleItemsDebug,
}: FooterProps) {
  const level = useSessionStore(state => state.securityLevel);
  const internalStaff = ['user','manager','admin','super_admin'].includes(level || '');
  const build = getCurrentAppBuildIdentity();
  const environmentLabel = displayEnvironment(build.environment);
  const portalVersion = portalBuildVersion(build.buildTime, build.commitSha || build.deploymentId);
  const [serviceBuild, setServiceBuild] = useState<ServiceStatusSnapshot>({
    health: 'checking',
    version: null,
    buildVersion: null,
    commit: null,
  });

  return (
    <footer className="app-footer">
      <div className="app-footer__identity">
        <span
          className="app-footer__copyright"
          aria-label={`Copyright ${new Date().getFullYear()} ITA AV Solutions`}
        >
          <span className="app-footer__copyright-long" aria-hidden="true">
            &copy; {new Date().getFullYear()} ITA AV Solutions
          </span>
          <span className="app-footer__copyright-short" aria-hidden="true">
            &copy; {new Date().getFullYear()} ITA
          </span>
        </span>
        <nav className="app-footer__links" aria-label="Footer links">
          <Link className="app-footer__changelog" to="/change-log">Change log</Link>
          <details className="app-footer__details">
            <summary>Build info</summary>
            <div className="app-footer__details-panel">
              <div className="app-footer__details-heading">Portal build</div>
              <dl className="app-footer__build-grid">
                <div className="app-footer__build-row">
                  <dt>Environment</dt>
                  <dd>{environmentLabel}</dd>
                </div>
                <div className="app-footer__build-row">
                  <dt>Build version</dt>
                  <dd><code>{portalVersion}</code></dd>
                </div>
                <div className="app-footer__build-row">
                  <dt>Portal commit</dt>
                  <dd><code>{build.commitSha || 'Local build'}</code></dd>
                </div>
                <div className="app-footer__build-row">
                  <dt>Deployment</dt>
                  <dd><code>{build.deploymentId || 'Not assigned'}</code></dd>
                </div>
                <div className="app-footer__build-row">
                  <dt>AI service</dt>
                  <dd>{serviceBuild.buildVersion || (serviceBuild.version ? `v${serviceBuild.version}` : serviceBuild.health)}</dd>
                </div>
                <div className="app-footer__build-row">
                  <dt>AI service commit</dt>
                  <dd><code>{serviceBuild.commit || 'Not reported'}</code></dd>
                </div>
              </dl>
              {showItemsDebugToggle ? (
                <button
                  type="button"
                  className="btn micro subtle app-footer__debug-toggle"
                  onClick={onToggleItemsDebug}
                  aria-pressed={itemsDebugEnabled}
                  title="Toggle Items Panel Debug Console (session only)"
                >
                  Items Debug: {itemsDebugEnabled ? 'ON' : 'OFF'}
                </button>
              ) : null}
            </div>
          </details>
        </nav>
        <div className="app-footer__health" aria-label="System status">
          <SupabaseStatus compact />
          {internalStaff && (<ServiceStatus compact onStatusChange={setServiceBuild} />)}
        </div>
      </div>
    </footer>
  );
}
